import type { inferProcedureOutput } from '@trpc/server'
import type { AppRouter } from '@viapeople/api'

// Filter operators enum - must be defined locally (enums are runtime values that can't be imported from server-only API package)
export enum FilterOperator {
	EQ = 'eq',
	NEQ = 'neq',
	CONTAINS = 'contains',
	IN = 'in',
	GT = 'gt',
	GTE = 'gte',
	LT = 'lt',
	LTE = 'lte',
	IS_NULL = 'is_null',
	IS_NOT_NULL = 'is_not_null',
	BETWEEN = 'between',
}

export enum FilterSource {
	USER_ATTRIBUTE = 'user_attribute',
	FORM_RESPONSE = 'form_response',
	SYSTEM_FIELD = 'system_field',
	FORM_COMPONENT = 'form_component',
}

// Type aliases - these must match the API types
export type ReportType = 'form' | 'cycle' | 'cross-cycle' | 'raw-data' | 'distribution'
export type SubjectType = 'form' | 'cycle' | 'user' | 'repeatable'
export type AggregateOption = 'competency' | 'overall_rating' | 'all_ratings' | 'raw'
export type VisualizationType = 'table' | 'chart' | 'distribution' | 'pivot'
export type ExportFormat = 'xlsx' | 'csv'
export type FilterLogic = 'AND' | 'OR'
export type GroupByPeriod = 'month' | 'quarter' | 'year'
export type GroupBySource = 'custom_field' | 'system_field' | 'component_label'

// V3: Component label for grouping by form component category
export interface ComponentLabel {
	componentId: string
	componentLabel: string
	componentName: string
	formId: string
	formName: string
	groupCategory: string | null
}
export type RowType = 'detail' | 'subtotal' | 'grand-total'
export type UserAttributeSource = 'participant' | 'evaluator'

export enum AggregationFunction {
	AVG = 'AVG',
	COUNT = 'COUNT',
	SUM = 'SUM',
	MIN = 'MIN',
	MAX = 'MAX',
}

export interface AxisField {
	field: string
	source: GroupBySource
	period?: GroupByPeriod
	order: number
}

export type ColumnField = AxisField

export interface ValueConfig {
	field: 'response'
	aggregation: AggregationFunction
	label: string
}

export interface FilterCondition {
	field: string
	operator: FilterOperator
	values: string[]
	source?: FilterSource
	componentId?: string
}

export interface UserAttributeColumn {
	field: string
	source: UserAttributeSource
	label?: string
}

export interface VisibleColumn {
	key: string
	header: string
	align: 'left' | 'right' | 'center'
	format: 'text' | 'number' | 'date' | 'badge'
}

export interface SubtotalConfig {
	showRowSubtotals: boolean
	showColumnSubtotals: boolean
	showGrandTotal: boolean
}

export interface PivotRow {
	rowValues: Record<string, string | null>
	cells: Record<string, number | null>
	rowType: RowType
}

export interface PivotReportResult {
	rowAxis: AxisField[]
	columnAxis: ColumnField[]
	valueConfigs: ValueConfig[]
	columnValues: string[]
	rows: PivotRow[]
	metadata: {
		totalRows: number
		totalColumns: number
		generatedAt: string
		forms: string[]
		cycles: string[]
		truncated?: boolean
	}
}

export interface RawDataRow {
	participantId: string
	participantName?: string
	participantEmail?: string
	formId: string
	formName: string
	cycleId?: string
	cycleName?: string
	repeatableId: string
	repeatableLabel: string
	instanceIndex: number
	fields: Record<string, string | number | null>
}

export interface RawDataColumn {
	id: string
	label: string
	type: 'text' | 'number' | 'date' | 'select'
}

export interface RawDataReportResult {
	data: RawDataRow[]
	metadata: {
		totalRows: number
		generatedAt: string
		forms: string[]
		cycles: string[]
		repeatables: string[]
		columns: RawDataColumn[]
		truncated?: boolean
	}
}

export interface DistributionRow {
	responseValue: string
	count: number
	percentage: number
	groupBy?: Record<string, string>
}

export interface RepeatableComponent {
	id: string
	label: string
	formId: string
	formName: string
	childComponents: RepeatableChildComponent[]
}

export interface RepeatableChildComponent {
	id: string
	label: string
	name: string
	order: number
}

// System fields types
export type SystemFieldType = 'date' | 'boolean' | 'select' | 'string' | 'number'

// Form component types for dynamic field detection
export type FormComponentType =
	| 'heading'
	| 'paragraph'
	| 'text'
	| 'textarea'
	| 'rating'
	| 'checkbox'
	| 'select'
	| 'number'
	| 'date'
	| 'divider'
	| 'container'
	| 'instructions'
	| '9boxrating'
	| 'ai_summary'
	| 'reviewviewer'
	| 'externallink'
	| 'average'
	| 'repeatable'

export interface DistributionReportResult {
	responseField: string
	responseFieldLabel: string
	componentId: string
	componentType: FormComponentType
	distribution: DistributionRow[]
	metadata: {
		totalResponses: number
		uniqueValues: number
		generatedAt: string
		forms: string[]
		cycles: string[]
	}
}

export interface AvailableField {
	id: string
	label: string
	type: SystemFieldType | 'text'
	source: FilterSource
	options?: string[]
}

export interface FormField {
	id: string
	formId: string
	formName: string
	componentType: FormComponentType
	label: string
	sectionLabel?: string
	options?: string[]
	isFilterable: boolean
	isGroupable: boolean
	isNumeric: boolean
}

// V2.2: Task completion status
export type TaskStatus = 'not-started' | 'in-progress' | 'completed' | 'declined' | 'rejected'

export interface ReportRow {
	participantId?: string
	participantEmail?: string
	participantName?: string | null
	formId: string
	formName: string
	cycleId?: string | null
	cycleName?: string | null
	title?: string | null
	department?: string | null
	competency?: string | null
	averageRating?: number | null
	ratingDistribution?: Record<number, number>
	count?: number | null
	groupKey?: string | null
	customFields?: Record<string, string>
	rowType?: RowType
	groupingLevel?: number
	reviewerName?: string | null
	reviewerId?: string | null
	reviewerEmail?: string | null
	containerLabel?: string | null
	componentType?: string | null
	response?: string | null
	submissionDate?: string | null
	taskStatus?: TaskStatus | null
	evaluatorRole?: string | null
}

export interface ReportResult {
	data: ReportRow[]
	metadata: {
		totalRows: number
		generatedAt: string
		forms: string[]
		cycles: string[]
		groupBy: string[]
		aggregateBy: string[]
		truncated?: boolean
		columns: VisibleColumn[]
	}
}

export interface ReportBuilderConfig {
	id?: string
	name: string
	description?: string
	reportType: ReportType
	subjectType?: SubjectType
	selectedFormIds: string[]
	selectedCycleIds: string[]
	selectedRepeatableIds?: string[]
	selectedUserAttributes?: UserAttributeColumn[]
	groupBy: string[]
	groupBySource?: GroupBySource
	groupByPeriod?: GroupByPeriod
	rowFields?: AxisField[]
	showSubtotals?: boolean
	columnFields?: ColumnField[]
	valueConfigs?: ValueConfig[]
	aggregateBy: AggregateOption[]
	filters: FilterCondition[]
	filterLogic: FilterLogic
	questionLabelMappings: Record<string, string>
	visualizationType?: VisualizationType
	exportFormat?: ExportFormat
	distributionComponentId?: string
	createdAt?: Date | string
	updatedAt?: Date | string
}

// Infer types from API router for type safety and single source of truth
export type ReportBuilderInitData = inferProcedureOutput<AppRouter['client']['reportBuilder']['initialize']>

export type GenerateReportResult = inferProcedureOutput<AppRouter['client']['reportBuilder']['generateReport']>

export type SaveConfigResult = inferProcedureOutput<AppRouter['client']['reportBuilder']['saveConfig']>

// Derived types from API responses
export type FormSummary = ReportBuilderInitData['forms'][number]
export type CycleSummary = ReportBuilderInitData['cycles'][number]
export type SavedReportConfig = ReportBuilderInitData['savedConfigs'][number]
export type ReportMetadata = GenerateReportResult['metadata']

// V2.2: Task completion status (client-only convenience type)

// V2.5: Limits (must match backend reportBuilder.types.ts)
export const MAX_PIVOT_ROWS = 500

// V3.1: Separator for composite column keys (columnValue__aggregation)
export const PIVOT_CELL_KEY_SEPARATOR = '__'

/**
 * V3.1: Create a composite cell key for multiple aggregations
 */
export function createPivotCellKey(columnValue: string, aggregation: AggregationFunction): string {
	return `${columnValue}${PIVOT_CELL_KEY_SEPARATOR}${aggregation}`
}

/**
 * V3.1: Parse a composite cell key to extract column value and aggregation
 */
export function parsePivotCellKey(cellKey: string): {
	columnValue: string
	aggregation: AggregationFunction | null
} {
	const lastSepIdx = cellKey.lastIndexOf(PIVOT_CELL_KEY_SEPARATOR)
	if (lastSepIdx > 0) {
		const candidate = cellKey.substring(lastSepIdx + PIVOT_CELL_KEY_SEPARATOR.length)
		if (Object.values(AggregationFunction).includes(candidate as AggregationFunction)) {
			return {
				columnValue: cellKey.substring(0, lastSepIdx),
				aggregation: candidate as AggregationFunction,
			}
		}
	}
	return { columnValue: cellKey, aggregation: null }
}

// V3.0: Cardinality validation result (client-only type for UI)
export interface CardinalityValidationResult {
	field: string
	distinctCount: number
	isValid: boolean
	maxAllowed: number
	warning?: string
	suggestions?: string[]
}

export const STANDARD_USER_ATTRIBUTES = [
	{ field: 'email', label: 'Email' },
	{ field: 'firstName', label: 'First Name' },
	{ field: 'lastName', label: 'Last Name' },
	{ field: 'department', label: 'Department' },
	{ field: 'title', label: 'Title' },
	{ field: 'location', label: 'Location' },
	{ field: 'manager', label: 'Manager' },
] as const

// Derived type from API response (for type safety)
export type AvailableFieldFromAPI = ReportBuilderInitData['availableFields'][number]

// Operators by field type - for UI display
export const OPERATORS_BY_TYPE: Record<string, { value: FilterOperator; label: string }[]> = {
	text: [
		{ value: FilterOperator.EQ, label: 'Equals' },
		{ value: FilterOperator.NEQ, label: 'Not equals' },
		{ value: FilterOperator.CONTAINS, label: 'Contains' },
		{ value: FilterOperator.IN, label: 'In list' },
	],
	string: [
		{ value: FilterOperator.EQ, label: 'Equals' },
		{ value: FilterOperator.NEQ, label: 'Not equals' },
		{ value: FilterOperator.CONTAINS, label: 'Contains' },
		{ value: FilterOperator.IN, label: 'In list' },
	],
	number: [
		{ value: FilterOperator.EQ, label: 'Equals' },
		{ value: FilterOperator.NEQ, label: 'Not equals' },
		{ value: FilterOperator.GT, label: 'Greater than' },
		{ value: FilterOperator.GTE, label: 'Greater than or equal' },
		{ value: FilterOperator.LT, label: 'Less than' },
		{ value: FilterOperator.LTE, label: 'Less than or equal' },
		{ value: FilterOperator.BETWEEN, label: 'Between' },
		{ value: FilterOperator.IS_NULL, label: 'Is empty' },
		{ value: FilterOperator.IS_NOT_NULL, label: 'Is not empty' },
	],
	date: [
		{ value: FilterOperator.EQ, label: 'On' },
		{ value: FilterOperator.GT, label: 'After' },
		{ value: FilterOperator.GTE, label: 'On or after' },
		{ value: FilterOperator.LT, label: 'Before' },
		{ value: FilterOperator.LTE, label: 'On or before' },
		{ value: FilterOperator.BETWEEN, label: 'Between' },
		{ value: FilterOperator.IS_NULL, label: 'Is empty' },
		{ value: FilterOperator.IS_NOT_NULL, label: 'Is not empty' },
	],
	boolean: [{ value: FilterOperator.EQ, label: 'Is' }],
	select: [
		{ value: FilterOperator.EQ, label: 'Equals' },
		{ value: FilterOperator.NEQ, label: 'Not equals' },
		{ value: FilterOperator.IN, label: 'Is one of' },
	],
}

/**
 * Resolve an operator code to a human-friendly label.
 * Iterates OPERATORS_BY_TYPE and returns the first match found (text-type label for ambiguous codes like 'eq').
 */
export function getOperatorLabel(operator: FilterOperator | string): string {
	for (const ops of Object.values(OPERATORS_BY_TYPE)) {
		const found = ops.find((o) => o.value === operator)
		if (found) return found.label
	}
	return operator
}

/**
 * Resolve a filter field identifier to a user-friendly label.
 * Strips prefixes (custom., form.) and resolves system fields and special identifiers.
 */
export function resolveFieldLabel(
	field: string,
	systemFields: { id: string; label: string }[] = GROUPABLE_SYSTEM_FIELDS,
): string {
	if (!field) return ''
	if (field === '__question_label__') return 'Form Question'
	const sys = systemFields.find((f) => f.id === field)
	if (sys) return sys.label
	if (field.startsWith('component_label.')) return `Category: ${field.replace('component_label.', '')}`
	if (field.startsWith('custom.')) return field.replace('custom.', '')
	if (field.startsWith('form.')) return field.replace('form.', '')
	return field
}

// Groupable field type
export type GroupableFieldType = 'categorical' | 'date_period'

export interface GroupableSystemField {
	id: string
	label: string
	type: SystemFieldType
	groupType: GroupableFieldType
	options?: string[]
}

// System fields that can be used for Group By
export const QUESTION_LABEL_FIELD_ID = '__question_label__' as const

export const GROUPABLE_SYSTEM_FIELDS: GroupableSystemField[] = [
	// Categorical fields - direct grouping
	{
		id: 'user.fullName',
		label: 'Employee Name',
		type: 'string',
		groupType: 'categorical',
	},
	{
		id: 'cycle.status',
		label: 'Cycle Status',
		type: 'select',
		options: ['draft', 'active', 'completed', 'archived'],
		groupType: 'categorical',
	},
	{
		id: 'evaluation.isCompleted',
		label: 'Evaluation Completed',
		type: 'boolean',
		groupType: 'categorical',
	},
	{
		id: 'submission.calculatedRole',
		label: 'Evaluator Role',
		type: 'select',
		options: ['Self', 'Peer', 'Manager', 'Direct Report', 'Skip Level', 'Dotted Line'],
		groupType: 'categorical',
	},
	// V2.2: Task completion status
	{
		id: 'task.status',
		label: 'Task Status',
		type: 'select',
		options: ['not-started', 'in-progress', 'completed', 'declined', 'rejected'],
		groupType: 'categorical',
	},

	// Date fields - requires period selection (month/quarter/year)
	{
		id: 'cycle.launchDate',
		label: 'Cycle Launch Date',
		type: 'date',
		groupType: 'date_period',
	},
	{
		id: 'cycle.createdAt',
		label: 'Cycle Created',
		type: 'date',
		groupType: 'date_period',
	},
	{
		id: 'evaluation.completedAt',
		label: 'Evaluation Completed Date',
		type: 'date',
		groupType: 'date_period',
	},
	{
		id: 'evaluation.createdAt',
		label: 'Evaluation Started',
		type: 'date',
		groupType: 'date_period',
	},
]

// Period options for date grouping
export const GROUP_BY_PERIOD_OPTIONS: { value: GroupByPeriod; label: string }[] = [
	{ value: 'month', label: 'By Month' },
	{ value: 'quarter', label: 'By Quarter' },
	{ value: 'year', label: 'By Year' },
]
